/**
 * SetupAccountPage — Multi-step onboarding wizard for invited users
 *
 * Steps:
 *   1. Welcome   — validate invite token, show admin/org info
 *   2. Register  — Hanko /registration flow (password + TOTP + optional passkey)
 *   3. Done      — call /invite/{token}/complete, auto-redirect to console
 *
 * Props:
 *   - token: invite token from ?invite= query param
 *   - onComplete: callback after successful setup
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import hanko from '../hanko-init';
import { validateInviteToken, completeInvite } from '../utils/accountApi';
import SamLogo from './SamLogo';

const STEPS = ['welcome', 'register', 'done'];

export default function SetupAccountPage({ token, onComplete }) {
    const [step, setStep] = useState('welcome');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [inviteInfo, setInviteInfo] = useState(null); // { email, name, org_name }
    const [hankoState, setHankoState] = useState(null); // current Hanko flow state for rendering
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [countdown, setCountdown] = useState(3);
    const registrationStartedRef = useRef(false);

    // ── WebAuthn preflight (standalone function, matching LoginPage pattern) ──
    const handlePreflight = async (state) => {
        console.log('[SetupAccount] preflight');
        const webauthnAvailable = !!(window.PublicKeyCredential);
        let platform = false, conditional = false;
        try { platform = webauthnAvailable && await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch (e) { /* ignore */ }
        try { conditional = webauthnAvailable && window.PublicKeyCredential.isConditionalMediationAvailable && await window.PublicKeyCredential.isConditionalMediationAvailable(); } catch (e) { /* ignore */ }
        return state.actions.register_client_capabilities({
            webauthn_available: webauthnAvailable,
            webauthn_platform_authenticator_available: platform,
            webauthn_conditional_mediation_available: conditional,
        }).run();
    };

    // ── Wait for Hanko session cookie to be set (matching LoginPage pattern) ──
    const waitForSession = useCallback(async () => {
        for (let i = 0; i < 15; i++) {
            if (hanko.session.isValid()) {
                console.log('[SetupAccount] session valid after', i * 200, 'ms');
                return true;
            }
            await new Promise(r => setTimeout(r, 200));
        }
        console.log('[SetupAccount] session wait timed out');
        return false;
    }, []);

    // ── Show state in UI for user input ──
    // setTimeout(0) breaks out of the Hanko SDK's synchronous dispatch chain
    // so React commits the state update in its own render cycle.
    const showState = useCallback((state) => {
        setTimeout(() => {
            if (state?.error?.message) setError(state.error.message);
            setHankoState(state);
            setLoading(false);
        }, 0);
    }, []);

    // Step 1: Validate the invite token
    useEffect(() => {
        if (step !== 'welcome') return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError('');
                const result = await validateInviteToken(token);
                const data = result?.data || result;
                if (cancelled) return;
                if (!data?.valid) {
                    setError(data?.reason || 'This invite link is invalid or has expired.');
                    setLoading(false);
                    return;
                }
                setInviteInfo(data);
                setLoading(false);
            } catch (err) {
                if (!cancelled) {
                    setError('Unable to validate invite. Please try again later.');
                    setLoading(false);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [token, step]);

    // ── Route a flow state result to the appropriate handler ──
    // This is needed when we call .run() outside the flow.init handler chain
    // (e.g., from button click handlers). The SDK only auto-routes results
    // that are returned from within the handler chain. Manual .run() calls
    // return the next state as a resolved promise — we must dispatch it ourselves.
    const handlersRef = useRef(null);

    const processFlowResult = useCallback(async (result) => {
        if (!result || !result.name) {
            console.warn('[SetupAccount] processFlowResult: no valid result', result);
            setLoading(false);
            return;
        }
        console.log('[SetupAccount] processFlowResult: routing state', result.name);
        const handlers = handlersRef.current;
        if (handlers && handlers[result.name]) {
            // Call the handler — if it returns a promise (another .run()), chase it
            const next = await handlers[result.name](result);
            if (next && next.name) {
                await processFlowResult(next);
            }
        } else {
            // No specific handler — show it as a generic state
            console.warn('[SetupAccount] processFlowResult: no handler for', result.name);
            showState(result);
        }
    }, [showState]);

    // ── Complete the invite after successful registration ──
    const finishRegistration = useCallback(async () => {
        console.log('[SetupAccount] registration success!');
        setLoading(true);
        setHankoState(null);

        // Wait for session cookie
        await waitForSession();

        // Get the Hanko user ID and complete the invite
        try {
            let hankoUserId = '';
            try {
                const user = await hanko.user.getCurrent();
                hankoUserId = user?.id || '';
            } catch (_) { /* ignore — will link by email fallback */ }
            await completeInvite(token, hankoUserId);
        } catch (err) {
            console.warn('[SetupAccount] completeInvite failed:', err);
            // Don't block the user — they can still proceed
        }
        setStep('done');
        setLoading(false);
    }, [token, waitForSession]);

    // Step 2: Start Hanko registration flow
    const startRegistration = useCallback(async () => {
        if (registrationStartedRef.current) return;
        registrationStartedRef.current = true;
        setStep('register');
        setError('');
        setLoading(true);

        // Build the handler map — store in ref so processFlowResult can access it
        const handlers = {
            onError: (err) => {
                console.error('[SetupAccount] Hanko flow error:', err);
                setError('Registration error: ' + (err?.message || 'Unknown error'));
                setLoading(false);
            },
            // WebAuthn capability check — must be a direct function reference
            preflight: handlePreflight,

            // Auto-submit the invited user's email
            registration_init: (state) => {
                console.log('[SetupAccount] registration_init — auto-submitting email:', inviteInfo?.email,
                    'available actions:', Object.keys(state?.actions || {}));
                // Try the correct action name first (Hanko uses register_login_identifier)
                if (inviteInfo?.email && state.actions?.register_login_identifier) {
                    return state.actions.register_login_identifier({ email: inviteInfo.email }).run();
                }
                // Fallback: try other known action names
                if (inviteInfo?.email && state.actions?.register) {
                    return state.actions.register({ email: inviteInfo.email }).run();
                }
                if (inviteInfo?.email && state.actions?.continue_with_login_identifier) {
                    return state.actions.continue_with_login_identifier({ email: inviteInfo.email }).run();
                }
                // Last resort: show the state for manual input
                console.warn('[SetupAccount] registration_init — no matching action, showing state');
                showState(state);
            },

            // Password creation — show form for user input
            password_creation: (state) => {
                console.log('[SetupAccount] password_creation — actions:', Object.keys(state?.actions || {}));
                showState(state);
            },

            // MFA method chooser — auto-advance to TOTP
            mfa_method_chooser: (state) => {
                console.log('[SetupAccount] mfa_method_chooser — actions:', Object.keys(state?.actions || {}));
                if (state.actions?.continue_to_otp_secret_creation) {
                    return state.actions.continue_to_otp_secret_creation().run();
                }
                if (state.actions?.skip) {
                    return state.actions.skip().run();
                }
                showState(state);
            },

            // OTP secret creation — show QR code for TOTP setup
            otp_secret_creation: (state) => {
                console.log('[SetupAccount] otp_secret_creation');
                showState(state);
            },
            mfa_otp_secret_creation: (state) => {
                console.log('[SetupAccount] mfa_otp_secret_creation');
                showState(state);
            },

            // TOTP verification — show code input
            login_otp: (state) => {
                console.log('[SetupAccount] login_otp (TOTP verification)');
                showState(state);
            },

            // Passcode confirmation (email or TOTP verification)
            passcode_confirmation: (state) => {
                console.log('[SetupAccount] passcode_confirmation');
                showState(state);
            },

            // Passkey onboarding — show chooser
            credential_onboarding_chooser: (state) => {
                console.log('[SetupAccount] credential_onboarding_chooser');
                showState(state);
            },

            // Passkey creation
            onboarding_create_passkey: (state) => {
                console.log('[SetupAccount] onboarding_create_passkey');
                showState(state);
            },

            // Auto-skip states that don't need user input
            onboarding_email: (state) => {
                console.log('[SetupAccount] onboarding_email — auto-skipping');
                return state.actions?.skip ? state.actions.skip().run() : showState(state);
            },
            onboarding_username: (state) => {
                console.log('[SetupAccount] onboarding_username — auto-skipping');
                return state.actions?.skip ? state.actions.skip().run() : showState(state);
            },
            device_trust: (state) => {
                console.log('[SetupAccount] device_trust — auto-skipping');
                return state.actions?.skip ? state.actions.skip().run() : showState(state);
            },

            // Registration complete
            success: (state) => {
                finishRegistration();
            },

            // Error state
            error: (state) => {
                console.error('[SetupAccount] error state:', state);
                showState(state);
            },
        };
        handlersRef.current = handlers;

        try {
            await hanko.flow.init('/registration', handlers);
        } catch (err) {
            console.error('[SetupAccount] flow.init failed:', err);
            setError('Failed to start registration: ' + (err?.message || 'Unknown error'));
            setLoading(false);
            registrationStartedRef.current = false;
        }
    }, [inviteInfo, token, showState, finishRegistration]);

    // Handle password submission
    // We MUST await .run() and route the result through processFlowResult,
    // because this is called from a button click — outside the flow.init
    // handler chain. Without routing, the next state is silently discarded.
    const handlePasswordSubmit = useCallback(async (e) => {
        e.preventDefault();
        console.log('[SetupAccount] handlePasswordSubmit called',
            'password length:', password?.length,
            'hankoState:', hankoState?.name,
            'actions:', hankoState?.actions ? Object.keys(hankoState.actions) : 'null');
        if (!password) {
            setError('Please enter a password.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== passwordConfirm) {
            setError('Passwords do not match.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            let result;
            if (hankoState?.actions?.register_password) {
                console.log('[SetupAccount] calling register_password.run()...');
                const actionObj = hankoState.actions.register_password({ new_password: password });
                console.log('[SetupAccount] actionObj:', actionObj?.href, 'has run:', typeof actionObj?.run);
                result = await actionObj.run();
                console.log('[SetupAccount] register_password result:', result?.name, result?.status);
            } else if (hankoState?.actions?.password_creation) {
                console.log('[SetupAccount] calling password_creation.run()...');
                result = await hankoState.actions.password_creation({ new_password: password }).run();
                console.log('[SetupAccount] password_creation result:', result?.name, result?.status);
            } else {
                console.error('[SetupAccount] No password action found! Actions:', hankoState?.actions);
                setError('Unable to set password — no action available.');
                setLoading(false);
                return;
            }
            // Route the result to the appropriate handler
            console.log('[SetupAccount] routing result to processFlowResult:', result?.name);
            await processFlowResult(result);
        } catch (err) {
            console.error('[SetupAccount] handlePasswordSubmit error:', err);
            console.error('[SetupAccount] error stack:', err?.stack);
            setError('Failed to set password: ' + (err?.message || 'Unknown error'));
            setLoading(false);
        }
    }, [password, passwordConfirm, hankoState, processFlowResult]);

    // Handle TOTP code submission — await and route result
    const handleTotpSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!totpCode) {
            setError('Please enter the verification code.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            let result;
            if (hankoState?.actions?.otp_code_verify) {
                result = await hankoState.actions.otp_code_verify({ otp_code: totpCode }).run();
            } else if (hankoState?.actions?.otp_code_validate) {
                result = await hankoState.actions.otp_code_validate({ otp_code: totpCode }).run();
            } else if (hankoState?.actions?.verify_totp) {
                result = await hankoState.actions.verify_totp({ code: totpCode }).run();
            } else if (hankoState?.actions?.verify_passcode) {
                result = await hankoState.actions.verify_passcode({ code: totpCode }).run();
            } else {
                setError('Unable to verify code — no action available.');
                setLoading(false);
                return;
            }
            await processFlowResult(result);
        } catch (err) {
            console.error('[SetupAccount] handleTotpSubmit error:', err);
            setError('Verification failed: ' + (err?.message || 'Unknown error'));
            setLoading(false);
        }
    }, [totpCode, hankoState, processFlowResult]);

    // Handle passkey skip — await and route result
    const handleSkipPasskey = useCallback(async () => {
        setLoading(true);
        try {
            if (hankoState?.actions?.skip) {
                const result = await hankoState.actions.skip().run();
                await processFlowResult(result);
            } else {
                setError('No skip action available.');
                setLoading(false);
            }
        } catch (err) {
            console.error('[SetupAccount] handleSkipPasskey error:', err);
            setError('Failed to skip: ' + (err?.message || ''));
            setLoading(false);
        }
    }, [hankoState, processFlowResult]);

    // Handle passkey setup — await and route result
    const handleSetupPasskey = useCallback(async () => {
        setLoading(true);
        try {
            let result;
            if (hankoState?.actions?.webauthn_generate_creation_options) {
                result = await hankoState.actions.webauthn_generate_creation_options().run();
            } else if (hankoState?.actions?.register_passkey) {
                result = await hankoState.actions.register_passkey().run();
            } else {
                setError('Passkey setup not available.');
                setLoading(false);
                return;
            }
            await processFlowResult(result);
        } catch (err) {
            console.error('[SetupAccount] handleSetupPasskey error:', err);
            setError('Passkey setup failed: ' + (err?.message || ''));
            setLoading(false);
        }
    }, [hankoState, processFlowResult]);

    // Navigate to console — strips ?invite= and does a full page load
    // so ProtectedRoute re-evaluates auth from scratch.
    const goToConsole = useCallback(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('invite');
        window.location.href = url.toString();
    }, []);

    // Done step — auto-redirect countdown
    useEffect(() => {
        if (step !== 'done' || loading) return;
        if (countdown <= 0) {
            goToConsole();
            return;
        }
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [step, loading, countdown, goToConsole]);

    // Manual continue (fallback button)
    const handleContinue = useCallback(() => {
        goToConsole();
    }, [goToConsole]);

    // ── Current step index for progress bar ──
    const stepIndex = STEPS.indexOf(step);

    // ── Helper: detect Hanko state categories ──
    const isTotpState = hankoState?.name === 'login_otp' || hankoState?.name === 'passcode_confirmation' || hankoState?.name === 'otp_secret_creation' || hankoState?.name === 'mfa_otp_secret_creation';
    const isPasskeyState = hankoState?.name === 'credential_onboarding_chooser' || hankoState?.name === 'onboarding_create_passkey';

    // Content-driven rendering: show content based on hankoState, not loading.
    // The spinner only shows when there's NO content to display yet.
    const hasContent = (step === 'welcome' && inviteInfo) ||
        (step === 'register' && hankoState) ||
        (step === 'done');

    return (
        <div style={styles.container}>
            {/* Inject keyframes */}
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes shimmer {
                    0% { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                @keyframes pulseGlow {
                    0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.15); }
                    50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.3); }
                }
                @keyframes gradientShift {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
                @keyframes checkPop {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                .setup-input:focus {
                    border-color: #818CF8 !important;
                    box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.15) !important;
                }
                .setup-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4) !important;
                }
                .setup-btn-secondary:hover {
                    background: rgba(255,255,255,0.08) !important;
                    border-color: rgba(255,255,255,0.2) !important;
                }
            `}</style>

            {/* Subtle background pattern */}
            <div style={styles.bgPattern} />

            <div style={styles.card}>
                {/* Animated top accent */}
                <div style={styles.accentBar} />

                {/* Logo + brand */}
                <div style={styles.logoRow}>
                    <SamLogo style={{ width: 44, height: 44 }} />
                    <span style={styles.brandName}>SAM SURVEYOR</span>
                </div>

                {/* Progress bar */}
                <div style={styles.progressTrack}>
                    <div style={{
                        ...styles.progressFill,
                        width: `${((stepIndex + 1) / STEPS.length) * 100}%`,
                    }} />
                </div>
                <div style={styles.progressLabels}>
                    {['Welcome', 'Account Setup', 'Ready'].map((label, i) => (
                        <span key={label} style={{
                            ...styles.progressLabel,
                            color: i <= stepIndex ? '#C7D2FE' : 'rgba(148,163,184,0.5)',
                            fontWeight: i === stepIndex ? 600 : 400,
                        }}>{label}</span>
                    ))}
                </div>

                {/* Error banner */}
                {error && (
                    <div style={styles.errorBanner}>
                        <span style={{ marginRight: 8 }}>⚠</span>{error}
                    </div>
                )}

                {/* Loading spinner */}
                {loading && !hasContent && (
                    <div style={styles.loadingWrap}>
                        <div style={styles.spinner} />
                    </div>
                )}

                {/* ── WELCOME STEP ── */}
                {step === 'welcome' && inviteInfo && (
                    <div style={{ ...styles.stepContent, animation: 'fadeInUp 0.6s ease-out' }}>
                        {/* Welcome emoji badge */}
                        <div style={styles.welcomeBadge}>👋</div>

                        <h2 style={styles.welcomeHeading}>
                            Welcome{inviteInfo.name ? `, ${inviteInfo.name}` : ''}!
                        </h2>

                        <p style={styles.welcomeSubtext}>
                            Your trial account on <strong style={{ color: '#E0E7FF' }}>
                            {inviteInfo.org_name || 'Sam Surveyor'}</strong> is ready.
                            Let's get you set up in under a minute.
                        </p>

                        {/* Feature highlights */}
                        <div style={styles.featureList}>
                            <div style={styles.featureItem}>
                                <span style={styles.featureIcon}>🔐</span>
                                <span style={styles.featureText}>Create a secure password</span>
                            </div>
                            <div style={styles.featureItem}>
                                <span style={styles.featureIcon}>🎨</span>
                                <span style={styles.featureText}>Design conversational surveys</span>
                            </div>
                            <div style={styles.featureItem}>
                                <span style={styles.featureIcon}>🚀</span>
                                <span style={styles.featureText}>Deploy across voice, web & SMS</span>
                            </div>
                        </div>

                        <button
                            className="setup-btn"
                            style={styles.primaryBtn}
                            onClick={startRegistration}
                        >
                            Get Started
                            <span style={{ marginLeft: 8, fontSize: 16 }}>→</span>
                        </button>

                        <p style={styles.trialNote}>
                            14-day free trial · No credit card required
                        </p>
                    </div>
                )}

                {/* ── REGISTER STEP: Password Creation ── */}
                {step === 'register' && hankoState?.name === 'password_creation' && (
                    <div style={{ ...styles.stepContent, animation: 'fadeInUp 0.5s ease-out' }}>
                        <div style={styles.stepIcon}>🔐</div>
                        <h2 style={styles.heading}>Create Your Password</h2>
                        <p style={styles.emailLabel}>
                            for <strong style={{ color: '#C7D2FE' }}>{inviteInfo?.email}</strong>
                        </p>
                        <form onSubmit={handlePasswordSubmit} style={styles.form}>
                            <input
                                className="setup-input"
                                type="password"
                                placeholder="Password (min 8 characters)"
                                value={password}
                                onChange={e => { setPassword(e.target.value); setError(''); }}
                                style={styles.input}
                                autoFocus
                                minLength={8}
                                autoComplete="new-password"
                            />
                            <input
                                className="setup-input"
                                type="password"
                                placeholder="Confirm password"
                                value={passwordConfirm}
                                onChange={e => { setPasswordConfirm(e.target.value); setError(''); }}
                                style={styles.input}
                                minLength={8}
                                autoComplete="new-password"
                            />
                            <button className="setup-btn" type="submit" style={styles.primaryBtn}>
                                Set Password
                            </button>
                        </form>
                    </div>
                )}

                {/* ── REGISTER STEP: TOTP / Verification Code ── */}
                {step === 'register' && isTotpState && (
                    <div style={{ ...styles.stepContent, animation: 'fadeInUp 0.5s ease-out' }}>
                        <div style={styles.stepIcon}>🛡️</div>
                        <h2 style={styles.heading}>
                            {(hankoState?.name === 'otp_secret_creation' || hankoState?.name === 'mfa_otp_secret_creation' || hankoState?.name === 'login_otp')
                                ? 'Two-Factor Authentication'
                                : 'Enter Verification Code'}
                        </h2>
                        {(hankoState?.name === 'otp_secret_creation' || hankoState?.name === 'mfa_otp_secret_creation' || hankoState?.name === 'login_otp') && (
                            <p style={styles.text}>
                                Scan the QR code with your authenticator app, then enter the 6-digit code.
                            </p>
                        )}
                        {(hankoState?.payload?.otp_image_source || hankoState?.payload?.totp_setup?.qr_code) && (
                            <div style={styles.qrWrap}>
                                <img
                                    src={hankoState.payload.otp_image_source || hankoState.payload.totp_setup?.qr_code}
                                    alt="TOTP QR Code"
                                    style={styles.qrImage}
                                />
                            </div>
                        )}
                        {(hankoState?.payload?.otp_secret || hankoState?.payload?.totp_setup?.secret) && (
                            <p style={styles.secretText}>
                                Manual entry: <code style={styles.secretCode}>{hankoState.payload.otp_secret || hankoState.payload.totp_setup?.secret}</code>
                            </p>
                        )}
                        <form onSubmit={handleTotpSubmit} style={styles.form}>
                            <input
                                className="setup-input"
                                type="text"
                                placeholder="Enter 6-digit code"
                                value={totpCode}
                                onChange={e => { setTotpCode(e.target.value); setError(''); }}
                                style={{ ...styles.input, textAlign: 'center', letterSpacing: 8, fontSize: 22 }}
                                autoFocus
                                autoComplete="one-time-code"
                                inputMode="numeric"
                                maxLength={6}
                            />
                            <button className="setup-btn" type="submit" style={styles.primaryBtn}>Verify</button>
                        </form>
                        {hankoState?.actions?.skip && (
                            <button
                                className="setup-btn-secondary"
                                style={{ ...styles.secondaryBtn, marginTop: 12 }}
                                onClick={handleSkipPasskey}
                            >
                                Skip for Now
                            </button>
                        )}
                    </div>
                )}

                {/* ── REGISTER STEP: Passkey chooser ── */}
                {step === 'register' && isPasskeyState && (
                    <div style={{ ...styles.stepContent, animation: 'fadeInUp 0.5s ease-out' }}>
                        <div style={styles.stepIcon}>🔑</div>
                        <h2 style={styles.heading}>Add a Passkey</h2>
                        <p style={styles.text}>
                            Sign in with your fingerprint, face, or device PIN — faster and more secure than passwords.
                        </p>
                        <div style={styles.btnRow}>
                            <button className="setup-btn" style={styles.primaryBtn} onClick={handleSetupPasskey}>
                                Add Passkey
                            </button>
                            <button className="setup-btn-secondary" style={styles.secondaryBtn} onClick={handleSkipPasskey}>
                                Skip for Now
                            </button>
                        </div>
                    </div>
                )}

                {/* ── REGISTER STEP: Generic / unknown Hanko state ── */}
                {step === 'register' && hankoState &&
                    hankoState.name !== 'password_creation' &&
                    !isTotpState &&
                    !isPasskeyState && (
                        <div style={{ ...styles.stepContent, animation: 'fadeInUp 0.5s ease-out' }}>
                            <div style={styles.stepIcon}>⚙️</div>
                            <h2 style={styles.heading}>Account Setup</h2>
                            <p style={styles.text}>Processing: {hankoState.name}</p>
                            <p style={styles.subtext}>
                                Available actions: {Object.keys(hankoState.actions || {}).join(', ') || 'none'}
                            </p>
                            {hankoState.actions?.skip && (
                                <button className="setup-btn-secondary" style={styles.secondaryBtn} onClick={handleSkipPasskey}>
                                    Continue
                                </button>
                            )}
                        </div>
                    )}

                {/* ── DONE STEP ── */}
                {step === 'done' && (
                    <div style={{ ...styles.stepContent, animation: 'fadeInUp 0.6s ease-out' }}>
                        <div style={{
                            ...styles.successBadge,
                            animation: 'checkPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                        }}>✓</div>
                        <h2 style={styles.welcomeHeading}>You're All Set!</h2>
                        <p style={styles.text}>
                            Your account is ready. Welcome to Sam Surveyor.
                        </p>
                        <p style={styles.countdownText}>
                            Entering the console in {countdown}...
                        </p>
                        <button className="setup-btn" style={styles.primaryBtn} onClick={handleContinue}>
                            Open Console Now
                        </button>
                    </div>
                )}

                {/* Error-only state */}
                {step === 'welcome' && !inviteInfo && error && (
                    <div style={{ ...styles.stepContent, animation: 'fadeInUp 0.5s ease-out' }}>
                        <div style={styles.stepIcon}>🔗</div>
                        <h2 style={styles.heading}>Link Expired</h2>
                        <p style={styles.text}>
                            This invite link is no longer valid. Please contact your administrator for a new invitation.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Styles ──
const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #0F172A 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: 20,
        position: 'relative',
        overflow: 'hidden',
    },
    bgPattern: {
        position: 'absolute',
        inset: 0,
        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(99,102,241,0.08) 0%, transparent 50%),
                          radial-gradient(circle at 80% 20%, rgba(168,85,247,0.06) 0%, transparent 50%),
                          radial-gradient(circle at 50% 50%, rgba(59,130,246,0.04) 0%, transparent 70%)`,
        pointerEvents: 'none',
    },
    card: {
        position: 'relative',
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(148, 163, 184, 0.1)',
        borderRadius: 20,
        padding: '0 40px 40px',
        maxWidth: 480,
        width: '100%',
        animation: 'pulseGlow 4s ease-in-out infinite',
        overflow: 'hidden',
    },
    accentBar: {
        height: 3,
        background: 'linear-gradient(90deg, #6366F1, #A855F7, #3B82F6, #6366F1)',
        backgroundSize: '200% 100%',
        animation: 'gradientShift 3s ease-in-out infinite',
        marginBottom: 32,
        marginLeft: -40,
        marginRight: -40,
    },
    logoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 24,
    },
    brandName: {
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: '0.12em',
        color: 'rgba(203, 213, 225, 0.7)',
    },
    progressTrack: {
        height: 3,
        backgroundColor: 'rgba(148, 163, 184, 0.1)',
        borderRadius: 2,
        marginBottom: 8,
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        background: 'linear-gradient(90deg, #6366F1, #818CF8)',
        borderRadius: 2,
        transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    progressLabels: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 28,
    },
    progressLabel: {
        fontSize: 11,
        letterSpacing: '0.02em',
        transition: 'color 0.3s',
    },
    errorBanner: {
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        color: '#FCA5A5',
        padding: '10px 14px',
        borderRadius: 10,
        fontSize: 13,
        marginBottom: 20,
        animation: 'fadeInUp 0.3s ease-out',
    },
    loadingWrap: {
        display: 'flex',
        justifyContent: 'center',
        padding: '40px 0',
    },
    spinner: {
        width: 36,
        height: 36,
        border: '3px solid rgba(148,163,184,0.15)',
        borderTopColor: '#818CF8',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
    },
    stepContent: {
        textAlign: 'center',
    },
    welcomeBadge: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(168,85,247,0.15))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 36,
        margin: '0 auto 20px',
        border: '1px solid rgba(99,102,241,0.2)',
    },
    welcomeHeading: {
        fontSize: 26,
        fontWeight: 700,
        color: '#F1F5F9',
        marginBottom: 8,
        lineHeight: 1.3,
    },
    welcomeSubtext: {
        fontSize: 15,
        color: '#94A3B8',
        lineHeight: 1.7,
        marginBottom: 28,
    },
    featureList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 32,
        textAlign: 'left',
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 16px',
        background: 'rgba(148, 163, 184, 0.04)',
        border: '1px solid rgba(148, 163, 184, 0.08)',
        borderRadius: 12,
    },
    featureIcon: {
        fontSize: 20,
        width: 32,
        textAlign: 'center',
        flexShrink: 0,
    },
    featureText: {
        fontSize: 14,
        color: '#CBD5E1',
        fontWeight: 500,
    },
    trialNote: {
        fontSize: 12,
        color: 'rgba(148, 163, 184, 0.5)',
        marginTop: 16,
        letterSpacing: '0.02em',
    },
    stepIcon: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'rgba(99,102,241,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 28,
        margin: '0 auto 16px',
        border: '1px solid rgba(99,102,241,0.15)',
    },
    heading: {
        fontSize: 22,
        fontWeight: 600,
        color: '#F1F5F9',
        marginBottom: 8,
    },
    text: {
        fontSize: 14,
        color: '#94A3B8',
        lineHeight: 1.7,
        marginBottom: 20,
    },
    subtext: {
        fontSize: 12,
        color: 'rgba(148,163,184,0.6)',
        marginBottom: 16,
    },
    emailLabel: {
        fontSize: 13,
        color: '#94A3B8',
        marginBottom: 20,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    input: {
        padding: '14px 16px',
        background: 'rgba(15, 23, 42, 0.6)',
        border: '1px solid rgba(148, 163, 184, 0.15)',
        borderRadius: 10,
        fontSize: 15,
        color: '#F1F5F9',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    primaryBtn: {
        padding: '14px 28px',
        background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 15px rgba(99, 102, 241, 0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    secondaryBtn: {
        padding: '14px 28px',
        background: 'rgba(148, 163, 184, 0.05)',
        color: '#94A3B8',
        border: '1px solid rgba(148, 163, 184, 0.15)',
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    btnRow: {
        display: 'flex',
        gap: 12,
        justifyContent: 'center',
    },
    successBadge: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(16,185,129,0.15))',
        border: '2px solid rgba(34,197,94,0.3)',
        color: '#4ADE80',
        fontSize: 32,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
    },
    countdownText: {
        fontSize: 13,
        color: 'rgba(148,163,184,0.5)',
        marginBottom: 20,
    },
    qrWrap: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 16,
    },
    qrImage: {
        width: 200,
        height: 200,
        borderRadius: 12,
        border: '1px solid rgba(148,163,184,0.15)',
        padding: 8,
        backgroundColor: '#fff',
    },
    secretText: {
        fontSize: 12,
        color: 'rgba(148,163,184,0.6)',
        marginBottom: 16,
        wordBreak: 'break-all',
    },
    secretCode: {
        backgroundColor: 'rgba(148,163,184,0.08)',
        padding: '2px 6px',
        borderRadius: 4,
        fontFamily: 'monospace',
        fontSize: 12,
        color: '#CBD5E1',
    },
};

